import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../lib/utils'

/* Ported from @roundhouse/ui. base-ui's Backdrop/Popup are Radix's
 * Overlay/Content, and its data-[starting-style] / data-[ending-style] become
 * data-[state=open] / data-[state=closed] driven by tw-animate-css. Same
 * 150ms, same curve, same transform-and-opacity-only rule. */
interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Width of the popup, e.g. 'w-[460px]'. */
  className?: string
  children: React.ReactNode
}

function Dialog({ open, onOpenChange, className, children }: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'bg-scrim fixed inset-0 z-50 duration-150 ease-out',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          data-slot="dialog"
          className={cn(
            'border-border bg-bg-elev shadow-modal fixed top-1/2 left-1/2 z-50 max-h-[90vh] w-[460px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border outline-none',
            'duration-150 ease-out',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
          )}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function DialogHeader({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex items-start justify-between gap-3 px-6 pt-5 pb-2', className)}
      {...props}
    >
      <div className="min-w-0">{children}</div>
      <DialogPrimitive.Close
        aria-label="Close"
        className="text-fg-subtle hover:bg-bg-subtle hover:text-fg focus-visible:outline-accent -mt-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md transition-colors duration-100 ease-out outline-none focus-visible:outline-2 focus-visible:outline-offset-1"
      >
        <X className="size-3.5" />
      </DialogPrimitive.Close>
    </div>
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-fg m-0 text-base font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-fg-muted mt-1 mb-0 text-sm', className)}
      {...props}
    />
  )
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-body" className={cn('px-6 py-4', className)} {...props} />
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex items-center justify-end gap-2 px-6 pt-2 pb-5', className)}
      {...props}
    />
  )
}

const DialogClose = DialogPrimitive.Close

export {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  type DialogProps,
}
